import { postSupportCallTranscriptSegment } from 'src/supportPortal/services/supportCallService'

// Opt-in, best-effort live transcription for the support call surface.
//
// Scope + honesty: the browser's SpeechRecognition (Web Speech API) transcribes the agent's
// own microphone only — it cannot hear the remote party, whose audio arrives over the WebRTC
// leg. Full two-sided, speaker-diarized transcription needs a server-side STT feed on the
// call recording; this module is the FE seam for that (swap the source, keep the same
// subscribe/emit contract + panel). Kept behind SUPPORT_CALL_TRANSCRIPTION_FLAG.
//
// Structured as a module-level singleton (like portalVoiceCallManager) so a live transcript
// survives the surface being minimized to the PIP and restored.

const TRANSCRIPTION_FLAG_STORAGE_KEY = 'aumhum.supportCall.transcriptionEnabled'

function getSpeechRecognitionConstructor() {
  if (typeof window === 'undefined') {
    return null
  }
  return window.SpeechRecognition || window.webkitSpeechRecognition || null
}

export function isSupportCallTranscriptionSupported() {
  return !!getSpeechRecognitionConstructor()
}

// Capability flag: the feature is available where the browser supports it AND it has not been
// explicitly disabled (localStorage flag === 'false'). Per-call opt-in still applies on top.
export function isSupportCallTranscriptionEnabled() {
  if (!isSupportCallTranscriptionSupported()) {
    return false
  }
  try {
    return window.localStorage?.getItem(TRANSCRIPTION_FLAG_STORAGE_KEY) !== 'false'
  } catch (error) {
    return true
  }
}

const listeners = new Set()
let recognition = null
let active = false
let restartGuard = false
let segmentCounter = 0
let activeContext = null

// Set/clear the call context (callSessionId + contact ids) so finalized agent-side segments are
// persisted server-side (POST /call/{callSessionId}/transcript/segment). Cleared on call end/close.
export function setSupportCallTranscriptionContext(context) {
  activeContext = context && context.callSessionId ? { ...context } : null
}

let state = {
  active: false,
  supported: isSupportCallTranscriptionSupported(),
  error: '',
  finals: [], // [{ id, speaker: 'agent', text, at }]
  interim: '',
}

function emit() {
  const snapshot = { ...state, finals: [...state.finals] }
  listeners.forEach((listener) => {
    try {
      listener(snapshot)
    } catch (error) {
      console.error('Support call transcription listener failed:', error)
    }
  })
}

function setState(patch) {
  state = { ...state, ...patch }
  emit()
}

export function getSupportCallTranscriptionState() {
  return { ...state, finals: [...state.finals] }
}

export function subscribeSupportCallTranscription(listener) {
  if (typeof listener !== 'function') {
    return () => {}
  }
  listeners.add(listener)
  listener(getSupportCallTranscriptionState())
  return () => {
    listeners.delete(listener)
  }
}

function handleResult(event) {
  let interim = ''
  const nextFinals = []
  for (let i = event.resultIndex; i < event.results.length; i += 1) {
    const result = event.results[i]
    const transcript = result?.[0]?.transcript || ''
    if (!transcript.trim()) {
      continue
    }
    if (result.isFinal) {
      segmentCounter += 1
      nextFinals.push({
        id: `seg-${segmentCounter}`,
        speaker: 'agent',
        text: transcript.trim(),
        at: new Date().toISOString(),
      })
    } else {
      interim += transcript
    }
  }

  if (nextFinals.length > 0) {
    setState({ finals: [...state.finals, ...nextFinals], interim: interim.trim() })
    persistFinals(nextFinals)
  } else {
    setState({ interim: interim.trim() })
  }
}

// Fire-and-forget persistence of finalized agent-side segments when a call context is set.
function persistFinals(finals) {
  const context = activeContext
  if (!context || !context.callSessionId || !finals || finals.length === 0) {
    return
  }
  finals.forEach((segment) => {
    const sequenceMatch = /seg-(\d+)/.exec(segment.id || '')
    void postSupportCallTranscriptSegment(context.callSessionId, {
      speaker: segment.speaker || 'agent',
      text: segment.text,
      finalSegment: true,
      sequence: sequenceMatch ? Number(sequenceMatch[1]) : undefined,
      source: 'agent-browser',
      bookedEventId: context.bookedEventId,
      patientId: context.patientId,
      practitionerId: context.practitionerId,
    })
  })
}

function handleError(event) {
  const errorType = event?.error || ''
  // "no-speech"/"aborted" are benign — the recognizer simply timed out; the onend restart keeps
  // it going. Surface only the actionable ones.
  if (errorType === 'not-allowed' || errorType === 'service-not-allowed') {
    setState({ error: 'Microphone access is required for live transcription.' })
    stopSupportCallTranscription()
  } else if (errorType && errorType !== 'no-speech' && errorType !== 'aborted') {
    setState({ error: `Transcription error: ${errorType}` })
  }
}

function handleEnd() {
  // The recognizer stops itself on silence; restart while the agent keeps it on.
  if (active && !restartGuard) {
    restartGuard = true
    try {
      recognition?.start()
    } catch (error) {
      // start() throws if called too soon after end(); retry once shortly.
      window.setTimeout(() => {
        try {
          if (active) {
            recognition?.start()
          }
        } catch (retryError) {
          console.warn('Unable to restart transcription:', retryError)
        }
      }, 250)
    }
    window.setTimeout(() => {
      restartGuard = false
    }, 400)
  }
}

export function startSupportCallTranscription({ lang } = {}) {
  if (!isSupportCallTranscriptionEnabled()) {
    setState({ error: 'Live transcription is not available in this browser.' })
    return false
  }
  if (active) {
    return true
  }

  const SpeechRecognition = getSpeechRecognitionConstructor()
  try {
    recognition = new SpeechRecognition()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = lang || (typeof navigator !== 'undefined' ? navigator.language : 'en-US') || 'en-US'
    recognition.onresult = handleResult
    recognition.onerror = handleError
    recognition.onend = handleEnd
    recognition.start()
    active = true
    setState({ active: true, error: '', supported: true })
    return true
  } catch (error) {
    console.warn('Unable to start live transcription:', error)
    setState({ error: 'Unable to start live transcription.', active: false })
    active = false
    recognition = null
    return false
  }
}

export function stopSupportCallTranscription() {
  active = false
  if (recognition) {
    try {
      recognition.onend = null
      recognition.stop()
    } catch (error) {
      // ignore
    }
    recognition = null
  }
  setState({ active: false, interim: '' })
}

export function clearSupportCallTranscription() {
  segmentCounter = 0
  setState({ finals: [], interim: '', error: '' })
}

// Plain-text export of the transcript for copy / attach-to-note.
export function getSupportCallTranscriptText() {
  return state.finals.map((segment) => `${segment.speaker === 'agent' ? 'Agent' : 'Caller'}: ${segment.text}`).join('\n')
}
